import { readonly, ref } from 'vue';
import { getPaymentBankOptions } from '@/services/paymentService';
import { useSuperAdminCreditorAccessStore } from '@/stores/superAdminCreditorAccess';
import { getCookieJSON, setCookie } from '@/utils/cookieUtils';

interface PaymentBankOptionItem {
    id: number;
    label: string;
}

/** PHASE1: `null` means All; an empty array means no Bank is selected. */
export type PaymentBankSelection = number[] | null;

interface PaymentBankFilterSettings {
    mode?: 'all' | 'selected';
    bankIds?: number[];
}

const FILTER_COOKIE_NAME = 'payments_bank_filter';
const FILTER_COOKIE_EXPIRY_DAYS = 365;

const options = ref<PaymentBankOptionItem[]>([]);
let loaded = false;
let loadedScopeKey: string | null = null;
let loadGeneration = 0;
let loadingRequest: Promise<void> | null = null;

function currentScopeKey(): string {
    const creditorId = useSuperAdminCreditorAccessStore().context?.creditorId;
    return creditorId != null ? `creditor:${creditorId}` : 'none';
}

function filterCookieName(): string {
    const creditorId = useSuperAdminCreditorAccessStore().context?.creditorId;
    return creditorId != null
        ? `${FILTER_COOKIE_NAME}_${creditorId}`
        : FILTER_COOKIE_NAME;
}

/**
 * Loads bank options for the current impersonation/creditor scope.
 * Refetches only when Payments mounts (or loadOptions is called) and the
 * cached scope differs from the current one.
 */
async function loadOptions(): Promise<void> {
    const scopeKey = currentScopeKey();
    if (loaded && loadedScopeKey === scopeKey) return;

    const generation = loadGeneration + 1;
    loadGeneration = generation;
    loaded = false;
    loadedScopeKey = null;
    options.value = [];
    loadingRequest = null;

    loadingRequest = getPaymentBankOptions()
        .then((banks) => {
            if (generation !== loadGeneration) return;

            options.value = banks.map((bank) => ({
                id: bank.id,
                label: bank.bankName,
            }));
            loaded = true;
            loadedScopeKey = scopeKey;
        })
        .catch(() => {
            if (generation !== loadGeneration) return;
            options.value = [];
        })
        .finally(() => {
            if (generation === loadGeneration) {
                loadingRequest = null;
            }
        });

    await loadingRequest;
}

export function usePaymentBankOptions() {
    return {
        options: readonly(options),
        loadOptions,
    };
}

function normalizeBankIds(ids: unknown): number[] {
    if (!Array.isArray(ids)) return [];

    return [...new Set(ids.filter(
        (id): id is number => Number.isInteger(id) && id > 0,
    ))];
}

export function loadSavedPaymentBankIds(): PaymentBankSelection {
    const saved = getCookieJSON<PaymentBankFilterSettings>(filterCookieName());
    if (!saved || saved.mode === 'all') return null;

    const bankIds = normalizeBankIds(saved.bankIds);

    if (saved.mode === 'selected') return bankIds;

    // PHASE1: Legacy cookies stored an empty array for All.
    return bankIds.length > 0 ? bankIds : null;
}

export function savePaymentBankIds(selection: PaymentBankSelection): void {
    const settings: PaymentBankFilterSettings = selection === null
        ? { mode: 'all' }
        : { mode: 'selected', bankIds: selection };

    setCookie(
        filterCookieName(),
        settings as unknown as Record<string, unknown>,
        { days: FILTER_COOKIE_EXPIRY_DAYS },
    );
}
